/**
 * Modal de ELIMINAÇÃO de dados (LGPD) — popup grande, explica tudo e exige a senha.
 * Mostra a prévia do que será apagado, pede a senha do app + confirmação, executa
 * e apresenta o relatório de exclusão.
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, Trash2, X } from "lucide-react";
import { useState } from "react";
import { api, ApiError } from "../lib/api";

interface Previa {
  nome: string;
  nome_pasta: string;
  sindico: string | null;
  n_competencias: number;
  meses: string[];
  n_pdfs: number;
  pasta: string;
  tamanho_mb: number;
}
interface Movido {
  ok: boolean;
  modo?: string;
  retencao_dias?: number;
}

export function EliminarDadosModal({
  condominioId,
  onFechar,
  onConcluido,
}: {
  condominioId: number;
  onFechar: () => void;
  onConcluido: () => void;
}) {
  const [senha, setSenha] = useState("");
  const [ciente, setCiente] = useState(false);
  const [movido, setMovido] = useState<Movido | null>(null);
  const [fase, setFase] = useState<"form" | "codigo">("form");
  const [codigo, setCodigo] = useState("");
  const [aprovMsg, setAprovMsg] = useState<string | null>(null);

  const { data: previa, isLoading } = useQuery<Previa>({
    queryKey: ["lgpd", "eliminar", "previa", condominioId],
    queryFn: () =>
      api.get<Previa>(`/lgpd/eliminar/condominio/${condominioId}/previa`),
  });

  // 1º passo: solicitar. Ou manda um código por e-mail (Dono/conselho), ou libera
  // o Dono a excluir direto (fallback quando não há e-mail/aprovadores).
  const solicitar = useMutation({
    mutationFn: () =>
      api.post<{ ok: boolean; modo?: string; aprovadores?: number; motivo?: string }>(
        `/lgpd/eliminar/condominio/${condominioId}/solicitar`, { senha },
      ),
    onSuccess: (r) => {
      if (r.ok && r.modo === "codigo") {
        setAprovMsg(
          `Enviamos um código de aprovação para o administrador (Dono) e o conselho` +
          (r.aprovadores ? ` — ${r.aprovadores} e-mail(s).` : ".") +
          " Peça o código a eles e digite abaixo.",
        );
        setFase("codigo");
      } else if (r.ok && r.modo === "direto") {
        eliminar.mutate(); // Dono, sem aprovadores → exclui direto
      } else {
        setAprovMsg(r.motivo ?? "Não foi possível iniciar a exclusão.");
      }
    },
  });

  const eliminar = useMutation({
    mutationFn: () =>
      api.post<Movido>(`/lgpd/eliminar/condominio/${condominioId}`, { senha, codigo }),
    onSuccess: (r) => setMovido(r),
  });

  const erroDe = (e: unknown) =>
    e instanceof ApiError
      ? ((e.body as { detail?: string })?.detail ?? e.message)
      : e ? "Não foi possível concluir." : null;
  const erro = erroDe(eliminar.error) ?? erroDe(solicitar.error);

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full overflow-hidden">
        {/* Cabeçalho vermelho */}
        <div className="bg-rose-600 text-white px-5 py-4 flex items-center gap-3">
          <AlertTriangle size={24} />
          <div className="flex-1">
            <h3 className="text-lg font-semibold">
              {movido ? "Enviado para a lixeira" : "Apagar condomínio — vai para a lixeira"}
            </h3>
          </div>
          <button onClick={onFechar} className="text-white/80 hover:text-white" aria-label="Fechar">
            <X size={20} />
          </button>
        </div>

        <div className="p-5">
          {isLoading && (
            <div className="flex items-center gap-2 text-slate-500 text-sm py-6 justify-center">
              <Loader2 size={16} className="animate-spin" /> Carregando…
            </div>
          )}

          {/* --- Resultado: foi para a lixeira --- */}
          {movido ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2 text-emerald-700">
                <CheckCircle2 size={18} className="mt-0.5" />
                <p className="text-sm">
                  <b>{previa?.nome}</b> foi movido para a <b>lixeira</b>. Ele some das telas,
                  mas o administrador (Dono) pode <b>restaurá-lo por {movido.retencao_dias ?? 30} dias</b>.
                  Passado o prazo, é apagado definitivamente.
                </p>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[13px] text-amber-900">
                Para restaurar ou apagar de vez antes do prazo, o Dono usa a tela
                <b> Lixeira</b> (botão no topo).
              </div>
              <button
                type="button"
                onClick={onConcluido}
                className="w-full px-4 py-2.5 bg-brand-700 hover:bg-brand-800 text-white text-sm font-semibold rounded-lg"
              >
                Concluir
              </button>
            </div>
          ) : (
            previa && (
              <div className="space-y-4">
                <p className="text-sm text-slate-700">
                  Você está prestes a mover <b>{previa.nome}</b>
                  {previa.sindico ? ` (síndico: ${previa.sindico})` : ""} para a{" "}
                  <b className="text-amber-700">lixeira</b>. Ele some das telas, mas pode ser{" "}
                  <b>restaurado pelo Dono por 30 dias</b>. Só depois desse prazo é apagado
                  de vez (direito de eliminação da LGPD).
                </p>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-[13px] text-amber-900 space-y-1">
                  <div className="font-semibold mb-1">Vai para a lixeira (recuperável 30 dias):</div>
                  <div>• {previa.n_competencias} mês(es) auditado(s){previa.meses.length ? ` (${previa.meses.join(", ")})` : ""}</div>
                  <div>• {previa.n_pdfs} PDF(s) de balancete e as imagens/leituras (OCR)</div>
                  <div>• Notas, impostos, cotas, fornecedores e histórico do condomínio</div>
                  <div className="text-[11px] text-amber-700/80 break-all pt-1">Pasta: {previa.pasta} ({previa.tamanho_mb} MB)</div>
                </div>

                <label className="flex items-start gap-2 text-[13px] text-slate-700">
                  <input
                    type="checkbox"
                    checked={ciente}
                    onChange={(e) => setCiente(e.target.checked)}
                    className="mt-0.5"
                  />
                  Entendo que o condomínio vai para a <b>lixeira</b> e que só o Dono pode
                  restaurá-lo, por 30 dias.
                </label>

                <div>
                  <label className="block text-[11px] font-medium text-slate-500 mb-1">
                    Digite a sua senha (de login) para confirmar
                  </label>
                  <input
                    type="password"
                    value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    disabled={fase === "codigo"}
                    className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm disabled:bg-slate-100"
                    placeholder="Senha"
                  />
                </div>

                {/* Aviso: aprovação de 2 pessoas (antes de solicitar) */}
                {fase === "form" && (
                  <div className="text-[12px] text-slate-700 bg-sky-50 border border-sky-200 rounded-md px-3 py-2 leading-snug">
                    <b>Aprovação de 2 pessoas.</b> Ao clicar em <b>Solicitar exclusão</b>,
                    enviaremos um <b>código de aprovação por e-mail</b> ao administrador
                    (Dono) e ao conselho. A exclusão só se conclui quando você digitar esse
                    código no passo seguinte.
                  </div>
                )}

                {/* Aprovação em 2 pessoas (código por e-mail) */}
                {fase === "codigo" && (
                  <div className="space-y-1.5">
                    {aprovMsg && (
                      <p className="text-[12px] text-slate-600 bg-sky-50 border border-sky-200 rounded-md px-3 py-2">
                        {aprovMsg}
                      </p>
                    )}
                    <label className="block text-[11px] font-medium text-slate-500">
                      Código de aprovação (6 dígitos)
                    </label>
                    <input
                      inputMode="numeric"
                      value={codigo}
                      onChange={(e) => setCodigo(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm tracking-widest text-center"
                      placeholder="______"
                    />
                  </div>
                )}
                {fase === "form" && aprovMsg && (
                  <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                    {aprovMsg}
                  </p>
                )}

                {erro && <p className="text-xs text-rose-700">{erro}</p>}

                <div className="flex gap-2 justify-end pt-1">
                  <button
                    type="button"
                    onClick={onFechar}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-900"
                  >
                    Cancelar
                  </button>
                  {fase === "form" ? (
                    <button
                      type="button"
                      onClick={() => { setAprovMsg(null); solicitar.mutate(); }}
                      disabled={!ciente || !senha || solicitar.isPending || eliminar.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-semibold rounded-md"
                    >
                      {solicitar.isPending || eliminar.isPending ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Trash2 size={15} />
                      )}
                      Solicitar exclusão
                    </button>
                  ) : (
                    <button
                      type="button"
                      onClick={() => eliminar.mutate()}
                      disabled={codigo.length < 6 || eliminar.isPending}
                      className="inline-flex items-center gap-1.5 px-4 py-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-40 text-white text-sm font-semibold rounded-md"
                    >
                      {eliminar.isPending ? (
                        <Loader2 size={15} className="animate-spin" />
                      ) : (
                        <Trash2 size={15} />
                      )}
                      Mover para a lixeira
                    </button>
                  )}
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
}
